import React from 'react';
import { CaseStudy } from '../types';
import {
  MobilePhoneIcon,
  StarIcon,
  ClockIcon,
  GlobeIcon,
  UsersIcon,
  ZapIcon,
  BarChartIcon,
  BrainIcon,
} from '../../../components/icons';

export const stylematchCaseStudy: CaseStudy = {
  metadata: {
    id: 'stylematch',
    slug: 'stylematch',
    template: 2,
    meta: {
      title: 'Stylematch by Atini Studio | Atini Studio',
      description: 'A Virtual Styling App Powered by Real Stylists and Smart Tech. Atini Studio partnered with Stylematch to bring to life an innovative mobile-first styling platform that redefines how users engage with personal fashion.',
      canonicalUrl: 'https://atinistudio.com/case-study/stylematch',
      ogImage: 'https://atinistudio.com/assets/atini-logo-colored.svg',
    },
  },
  data: {
    bannerHeader: {
      pillLabel: 'Mobile App | Fashion Tech',
      title: 'Stylematch by Atini Studio',
      description: 'A Virtual Styling App Powered by Real Stylists and Smart Tech. Atini Studio partnered with Stylematch to bring to life an innovative mobile-first styling platform that redefines how users engage with personal fashion. Designed for individuals seeking authentic, real-time fashion guidance, Stylematch blends smart technology with human expertise to offer an interactive, highly personalized styling experience—right from their phones.',
      image: 'stylematch/image-1.png',
      images: ['stylematch/image-1.png', 'stylematch/image-2.png', 'stylematch/image-3.png'],
      imageAlt: 'Stylematch App',
      primaryButton: {
        label: 'Visit Website',
        onClick: () => window.open('https://www.stylematch.co/', '_blank'),
      },
      secondaryButton: {
        label: 'App Store',
        onClick: () => window.open('https://apps.apple.com/us/app/stylematch-lookbook-styling/id6452011839', '_blank'),
      },
    },
    dataListItems: [
      {
        value: 'Mobile-First',
        label: 'Platform',
        icon: <MobilePhoneIcon />,
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: 'AI-Powered',
        label: 'Background Removal',
        icon: <BrainIcon />,
        gradientFrom: 'from-[#F5CA09]',
        gradientTo: 'to-[#AEAB00]',
      },
      {
        value: 'Real-Time',
        label: 'Stylist Feedback',
        icon: <ClockIcon />,
        gradientFrom: 'from-[#AEAB00]',
        gradientTo: 'to-[#FA832E]',
      },
      {
        value: 'Cross-Platform',
        label: 'iOS & Android',
        icon: <GlobeIcon />,
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
    ],
    challengeApproach: {
      challengeTitle: 'Project Overview',
      challengeDescription: 'The vision behind Stylematch was simple yet powerful: to create a virtual space where anyone could receive honest, tailored style advice from professional stylists. To execute that vision, Atini Studio led the end-to-end design and development of the app, ensuring an intuitive, responsive interface and a robust backend that could scale as the user base grew. Built for Performance and Flexibility: Developed using React Native for the mobile app, Node.js for backend logic, and hosted on Amazon Web Services (AWS), Stylematch is engineered for both performance and scalability. React Native enables consistent cross-platform support for iOS and Android devices, while AWS ensures fast, secure data handling and reliability across regions. This technology stack ensures smooth user interactions while enabling real-time capabilities.',
      requirementsTitle: 'Key Requirements',
      requirements: [
        { icon: <UsersIcon />, text: 'Photo-based styling requests with easy upload functionality' },
        { icon: <ZapIcon />, text: 'AI-powered background removal for clean outfit visualization' },
        { icon: <BarChartIcon />, text: 'Custom admin dashboard for stylist and user management' },
        { icon: <GlobeIcon />, text: 'Cross-platform mobile support for iOS and Android' },
      ],
      approachTitle: 'Development Approach',
      approachSections: [
        { title: 'Design & Ideation', description: 'Atini Studio worked closely with the Stylematch team throughout the project lifecycle—from ideation and wireframing to testing and deployment. Multiple design sprints and user flow revisions ensured an intuitive, elegant experience.' },
        { title: 'Mobile Development', description: 'Built using React Native for consistent cross-platform support, enabling smooth user interactions and real-time capabilities across iOS and Android devices.' },
        { title: 'Backend Infrastructure', description: 'Node.js backend hosted on Amazon Web Services (AWS) ensures fast, secure data handling and reliability across regions, engineered for both performance and scalability.' },
        { title: 'AI Integration', description: 'AI-powered background removal feature streamlines the feedback process by isolating outfits from noisy environments, giving stylists a clean canvas to work with.' },
        { title: 'Admin Dashboard', description: 'Full-featured admin panel allows Stylematch staff to manage stylist assignments, monitor user activity, track engagement metrics, and enable stylists to respond to requests and maintain client relationships.' },
      ],
      testimonial: {
        name: 'Elizabeth S.',
        role: 'Stylematch',
        quote: 'Christine was a dream to work with. She was great at communicating and met every deadline over the course of many months. She was also a good thought partner when it came to brainstorming the \'quick and dirty\' way to test / add new features. I really can\'t recommend her enough. I will be back to work with her again I\'m sure!',
        icon: 'stylematch/logo.png',
      },
    },
    platformFeatures: [
      {
        title: 'Photo-Based Styling Requests',
        description: 'Users can easily upload outfit photos directly through the app to receive thoughtful, constructive feedback and curated fashion suggestions from vetted professional stylists.',
        stat: 'Real-time feedback',
      },
      {
        title: 'AI Background Removal',
        description: 'To streamline the feedback process and ensure clarity, the app incorporates AI-powered background removal. This feature isolates outfits from noisy environments, giving stylists a clean canvas to work with.',
        stat: 'AI-powered',
      },
      {
        title: 'Custom Admin Dashboard',
        description: 'Beyond the user-facing app, Atini Studio built a full-featured admin panel that allows Stylematch staff to manage stylist assignments, monitor user activity, and track engagement metrics. The dashboard also enables stylists to respond to requests, update lookbooks, and maintain personalized client relationships.',
        stat: 'Full-featured admin',
      },
    ],
    technologies: [
      {
        name: 'React Native',
        description: 'Cross-platform mobile app development',
        logo: 'react_native_logo.svg',
      },
      {
        name: 'Nest.js',
        description: 'Backend framework and API layer',
        logo: 'tech-icons/nestjs.svg',
      },
      {
        name: 'PostgreSQL',
        description: 'Relational database',
        logo: 'tech-icons/postgresql.svg',
      },
      {
        name: 'Socket.io',
        description: 'Real-time communication',
        logo: 'tech-icons/socket.io.svg',
      },
      {
        name: 'Stripe',
        description: 'Payment processing',
        logo: 'tech-icons/stripe.svg',
      },
      {
        name: 'Amazon Web Services',
        description: 'Cloud hosting and infrastructure',
        logo: 'aws_logo.svg',
      },
    ],
    securityTitle: 'Security & Compliance',
    securityDescription: 'Secure data handling with AWS infrastructure, encrypted user data, secure photo uploads, and reliable cross-region hosting for optimal performance and security.',
  },
};

